import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { TruckOwnerService } from '../truck-owner.service';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-quote-bid',
  templateUrl: './quote-bid.component.html',
  styleUrls: ['./quote-bid.component.css']
})
export class QuoteBidComponent implements OnInit {
  bidForm: FormGroup;
  isLoading = false;
  token: any;
  trucksList: any = [];
  truckControl = new FormControl('', Validators.required);
  filteredOptions!: Observable<any[]>;
  selectedTruck: any;
  countdownTimer: string = '';
  intervalId: any;

  constructor(
    private dialogRef: MatDialogRef<QuoteBidComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private services: TruckOwnerService,
    private snackBar: MatSnackBar
  ) {
    this.bidForm = this.fb.group({
      price: [this.data?.participatedPrice || '', [Validators.required, Validators.min(1)]],
      truck_number: this.truckControl,
      driver_ref_id: [this.data?.participatedDriverRefId || '', Validators.required],
      trip_id: [this.data?.sourceId || this.data?.quoteId || '']
    });

    if (this.data?.participatedTruckNumber) {
      this.truckControl.setValue(this.data.participatedTruckNumber);
      this.truckControl.disable();
    }
    if (this.data?.participatedPrice) {
      this.bidForm.get('price')?.disable();
    }
  }

  ngOnInit(): void {
    this.token = sessionStorage.getItem("to_token");
    this.getTrcuksList();
    if (this.data?.expiresAt) {
      this.startCountdown(this.data.expiresAt);
    }

    this.filteredOptions = this.truckControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || ''))
    );
  }

  private _filter(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.trucksList.filter((option: any) =>
      option.truck_number.toLowerCase().includes(filterValue)
    );
  }

  onOptionSelected(event: any): void {
    const selectedTruckNumber = event.option.value;
    this.selectedTruck = this.trucksList.find(
      (truck: any) => truck.truck_number === selectedTruckNumber
    );
    if (this.selectedTruck) {
      // Extract driver_ref_id from driverInfo[0]._id as per user request
      const driverId = this.selectedTruck.driver_id || '';
      this.bidForm.patchValue({
        driver_ref_id: driverId
      });
    }
  }

  getTrcuksList() {
    if (this.data?.sourceId) {
      this.services
        .getQuoteTrucks(this.token, this.data.sourceId)
        .subscribe(
          (response: any) => {
            this.trucksList = response.body?.data || [];
            // Trigger filter update after data is loaded
            this.truckControl.setValue(this.truckControl.value);
            if (this.trucksList.length === 0) {
              this.truckControl.setErrors({ noTrucks: true });
              this.truckControl.markAsTouched();
            }
          },
          (error) => {
            this.trucksList = [];
            this.truckControl.setErrors({ noTrucks: true });
            this.truckControl.markAsTouched();
          }
        );
    } else if (this.data?.categoryId) {
      this.services
        .getAvailableTrucks(this.token, this.data.categoryId)
        .subscribe(
          (response: any) => {
            this.trucksList = response.body?.data || [];
            // Trigger filter update after data is loaded
            this.truckControl.setValue(this.truckControl.value);
            if (this.trucksList.length === 0) {
              this.truckControl.setErrors({ noTrucks: true });
              this.truckControl.markAsTouched();
            }
          },
          (error) => {
            this.trucksList = [];
            this.truckControl.setErrors({ noTrucks: true });
            this.truckControl.markAsTouched();
          }
        );
    }
  }

  onSubmit(): void {
    if (this.bidForm.valid) {
      this.isLoading = true;
      const payload: any = {
        quote_id: this.data.metadata?.quote_id || this.data.quoteId,
        price: this.bidForm.value.price,
        driver_ref_id: this.bidForm.value.driver_ref_id,
        trip_id: this.bidForm.value.trip_id,
        expiration_time: this.data?.expiresAt
      };

      if (this.data.isEdit) {
        payload.is_edit = true;
        payload.old_driver_ref_id = this.data.participatedDriverRefId;
      }

      const targetId = this.data._id || this.data.quoteId;
      this.services.quoteBid(this.token, targetId, payload).subscribe(
        (res: any) => {
          this.isLoading = false;
          if (res.status === 200 || res.status === 201) {
            this.snackBar.open(res.body?.message || 'Bid submitted successfully!', 'Success', { duration: 5000 });
            this.dialogRef.close('success');
          } else {
            this.snackBar.open(res.body?.message || 'Failed to submit bid', 'Fail', { duration: 5000 });
          }
        },
        (error) => {
          this.isLoading = false;
          console.error('Error submitting bid:', error);
          const errorMessage = error.error?.message || error.message || 'Error submitting bid. Please try again.';
          this.snackBar.open(errorMessage, 'Fail', { duration: 5000 });
        }
      );
    }
  }

  deleteBid(): void {
    if (window.confirm('Are you sure you want to delete this bid?')) {
      this.isLoading = true;
      const dro_id = this.data.participatedBidId;
      this.services.cancelDro({}, this.token, dro_id).subscribe(
        (res: any) => {
          this.isLoading = false;
          if (res.status === 200 || res.status === 201) {
            this.snackBar.open('Bid cancelled successfully', 'Success', { duration: 5000 });
            this.dialogRef.close('success');
          } else {
            this.snackBar.open('Failed to cancel bid', 'Fail', { duration: 5000 });
          }
        },
        (error) => {
          this.isLoading = false;
          console.error('Error deleting bid:', error);
          const errorMessage = error.error?.message || error.message || 'Error deleting bid. Please try again.';
          this.snackBar.open(errorMessage, 'Fail', { duration: 5000 });
        }
      );
    }
  }

  enableEdit(): void {
    this.data.isEdit = true;
    this.truckControl.enable();
    this.bidForm.get('price')?.enable();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  startCountdown(expiryTimeStr: string) {
    const expiryTime = new Date(expiryTimeStr).getTime();

    // Initial calculation so it doesn't wait 1s to show
    this.updateCountdown(expiryTime);

    this.intervalId = setInterval(() => {
      this.updateCountdown(expiryTime);
    }, 1000);
  }

  updateCountdown(expiryTime: number) {
    const currentTime = Date.now();
    const remainingTime = expiryTime - currentTime;

    if (remainingTime <= 0) {
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
      this.countdownTimer = "Expired";
      // optionally disable form if expired
      if (!this.data?.participatedPrice) {
        this.bidForm.disable();
      }
    } else {
      const totalMinutes = Math.floor(remainingTime / (1000 * 60));
      const seconds = Math.floor((remainingTime / 1000) % 60);
      const formattedMinutes = String(totalMinutes).padStart(2, "0");
      const formattedSeconds = String(seconds).padStart(2, "0");

      this.countdownTimer = `${formattedMinutes}:${formattedSeconds}`;
    }
  }

  ngOnDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}
